import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Alert,
  Button,
  Collapse,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Paper,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  ContentCopy,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Error,
  Schedule,
  PlayCircle,
  SkipNext,
  DataObject,
  Code,
  BugReport,
  Timeline,
  Memory
} from '@mui/icons-material';
import moment from 'moment';

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
      return 'primary';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'default';
    default:
      return 'default';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return <CheckCircle />;
    case 'running':
      return <PlayCircle />;
    case 'failed':
      return <Error />;
    case 'skipped':
      return <SkipNext />;
    default:
      return <Schedule />;
  }
};

const NodeExecutionDetails = ({ log, nodeId }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copiedText, setCopiedText] = useState('');

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const formatJSON = (data) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };

  if (!log) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            No execution data available for this node yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className='node-execution-details'>
        <h4>{log.nodeName || nodeId}</h4>
        <p>Node ID: {nodeId} • Type: {log.nodeType}
          <Chip
            icon={getStatusIcon(log.status)}
            label={log.status?.toUpperCase() || 'PENDING'}
            color={getStatusColor(log.status)}
            size="small"
          />
        </p>
        {/* Error Alert */}
          {log.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                Error: {log.error.message}
              </Typography>
              {log.error.code && (
                <Typography variant="caption" display="block">
                  Code: {log.error.code}
                </Typography>
              )}
            </Alert>
          )}
        <div className='detail-output mt-3'>
           <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'var(--sa-dark-surface-light)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Input</Typography>
                      <Tooltip title={copiedText === 'input' ? 'Copied!' : 'Copy'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(formatJSON(log.input), 'input')}
                        >
                          <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{
                      backgroundColor: 'var(--sa-dark-surface-light)',
                      p: 1,
                      borderRadius: 1,
                      maxHeight: 300,
                      overflow: 'auto'
                    }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace'
                      }}>
                        {formatJSON(log.input)}
                      </pre>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, backgroundColor: 'var(--sa-dark-surface-light)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">Output</Typography>
                      <Tooltip title={copiedText === 'output' ? 'Copied!' : 'Copy'}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(formatJSON(log.output), 'output')}
                        >
                          <ContentCopy sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{
                      backgroundColor: 'var(--sa-dark-surface-light)',
                      p: 1,
                      borderRadius: 1,
                      maxHeight: 300,
                      overflow: 'auto'
                    }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace'
                      }}>
                        {formatJSON(log.output)}
                      </pre>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
        </div>
      </div>
    </>
  );
};

export default NodeExecutionDetails;